import { useCanManageCompany } from "@/hooks/useCanManageCompany";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Recycle, CheckCircle, Package, Loader2, MoreVertical, Trash2, Lightbulb, Copy, Check, Loader } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { InventorySortMenu, type SortEntry, type SortFieldDef } from "@/components/InventorySortMenu";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

import { format } from "date-fns";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { WorkOrderScanSearch } from "@/components/WorkOrderScanSearch";
import { AssigneePicker, type CompanyMember } from "@/components/AssigneePicker";
import { fetchCompanyMembers } from "@/lib/companyMembers";
import { useCurrentCompany } from "@/hooks/useCurrentCompany";
import { useTranslation } from "react-i18next";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { applianceCategoryLabel } from "@/lib/applianceCategoryDisplay";
import {
  DELETE_REASONS,
  buildDeleteReason,
  refusalCodeFromError,
  type DeleteCheckResult,
  type DeleteReason,
} from "@/lib/workOrderDelete";

interface WorkOrderProgressData {
  currentStepIndex?: number;
  completedSteps?: number[];
  currentPhaseName?: string;
  currentPhaseCategory?: string;
  currentStepTitle?: string;
  totalSteps?: number;
  completedCount?: number;
}

interface WorkOrder {
  id: string;
  type: string;
  status: string | null;
  created_at: string | null;
  updated_at: string | null;
  completed_at: string | null;
  notes: string | null;
  data: WorkOrderProgressData | null;
  appliance_id: string;
  assignee_id: string | null;
  appliance?: {
    name: string | null;
    model_number: string;
    brand: string | null;
    category: string | null;
    serial_number: string | null;
    type?: string | null;
    product_type?: string | null;
    image_url: string | null;
  };
}

const statusColors: Record<string, { bg: string; text: string }> = {
  in_progress: { bg: "bg-yellow-100", text: "text-yellow-700" },
  completed: { bg: "bg-green-100", text: "text-green-700" },
};

const statusLabels: Record<string, string> = {
  in_progress: "IN PROGRESS",
  completed: "COMPLETED",
};

type StatusFilter = "in_progress" | "completed";

type WoSortKey = "lastTouched" | "created" | "appliance" | "id";

const WORK_ORDERS_SORT_FIELDS: readonly SortFieldDef<WoSortKey>[] = [
  {
    key: "lastTouched",
    label: "Last touched",
    directions: { desc: "Newest → Oldest", asc: "Oldest → Newest" },
    defaultDir: "desc",
  },
  {
    key: "created",
    label: "Created",
    directions: { desc: "Newest → Oldest", asc: "Oldest → Newest" },
    defaultDir: "desc",
  },
  {
    key: "appliance",
    label: "Appliance",
    directions: { asc: "A → Z", desc: "Z → A" },
    defaultDir: "asc",
  },
  {
    key: "id",
    label: "Work order ID",
    directions: { asc: "A → Z", desc: "Z → A" },
    defaultDir: "asc",
  },
];

const DEFAULT_WO_SORT: SortEntry<WoSortKey>[] = [{ key: "lastTouched", dir: "desc" }];

const isHarvest = (t: string) => t === "harvest" || t === "harvesting";


export default function WorkOrders() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { allowed: isAdmin } = useCanManageCompany();
  const { id: companyId, loading: companyLoading } = useCurrentCompany();
  const { toast } = useToast();
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("in_progress");
  const [searchQuery, setSearchQuery] = useState("");
  const [sort, setSort] = useState<SortEntry<WoSortKey>[]>(DEFAULT_WO_SORT);


  const [members, setMembers] = useState<CompanyMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(true);

  const [deleteOrder, setDeleteOrder] = useState<WorkOrder | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteReason, setDeleteReason] = useState<DeleteReason | null>(null);
  const [deleteOther, setDeleteOther] = useState("");
  const [deleteChecks, setDeleteChecks] = useState<Record<string, DeleteCheckResult>>({});
  const { t } = useTranslation("workOrders");
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const fetchWorkOrders = useCallback(async () => {
    if (!user || companyLoading) return;
    setIsLoading(true);
    if (!companyId) {
      setWorkOrders([]);
      setIsLoading(false);
      return;
    }
    const { data, error } = await supabase
      .from("work_orders")
      .select(
        "*, appliance:appliances(name, model_number, brand, category, product_type, image_url, serial_number)",
      )
      .eq("company_id", companyId)
      .is("deleted_at", null)
      .order("updated_at", { ascending: false });

    if (error) {
      console.error("Failed to fetch work orders:", error);
    } else {
      const harvestOnly = ((data as unknown as WorkOrder[]) || []).filter((w) =>
        isHarvest(w.type),
      );
      setWorkOrders(harvestOnly);
    }
    setIsLoading(false);
  }, [user, companyId, companyLoading]);

  useEffect(() => {
    fetchWorkOrders();
  }, [fetchWorkOrders]);

  useEffect(() => {
    let cancelled = false;
    const loadMembers = async () => {
      setMembersLoading(true);
      const list = await fetchCompanyMembers();
      if (!cancelled) {
        setMembers(list);
        setMembersLoading(false);
      }
    };
    if (user) loadMembers();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const loadDeleteCheck = async (orderId: string) => {
    setDeleteChecks((prev) => { const n = { ...prev }; delete n[orderId]; return n; });
    const { data, error } = await supabase.rpc("work_order_delete_check", { _work_order_id: orderId });
    setDeleteChecks((prev) => ({
      ...prev,
      [orderId]: error ? "not_allowed" : ((data as DeleteCheckResult) ?? "not_allowed"),
    }));
  };

  const handleDelete = async () => {
    if (!deleteOrder) return;
    const reason = buildDeleteReason(deleteReason, deleteOther);
    if (!reason) return;
    setIsDeleting(true);
    try {
      const { error } = await supabase.rpc("soft_delete_work_order", {
        _work_order_id: deleteOrder.id,
        _reason: reason,
      });
      if (error) {
        const code = refusalCodeFromError(error.message);
        toast({ title: t("softDelete.failed"), description: t(`softDelete.errors.${code}`), variant: "destructive" });
        return;
      }
      toast({ title: t("softDelete.deleted") });
      setDeleteOrder(null);
      await fetchWorkOrders();
    } finally {
      setIsDeleting(false);
    }
  };

  const inProgress = workOrders.filter((wo) => (wo.status || "in_progress") === "in_progress");
  const completed = workOrders.filter((wo) => wo.status === "completed");


  const statusFilterOptions: { value: StatusFilter; label: string; dotColor: string; count: number }[] = [
    { value: "in_progress", label: "In Progress", dotColor: "bg-yellow-500", count: inProgress.length },
    { value: "completed", label: "Completed", dotColor: "bg-green-500", count: completed.length },
  ];

  const filteredWorkOrders = workOrders.filter((w) => {
    const s = (w.status || "in_progress") as StatusFilter;
    if (s !== statusFilter) return false;
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    const a = w.appliance;
    const haystack = [
      a?.name,
      a?.model_number,
      a?.brand,
      a?.serial_number,
      a?.category,
      w.notes,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });

  const sortedWorkOrders = useMemo(() => {
    const arr = [...filteredWorkOrders];
    arr.sort((a, b) => {
      for (const s of sort) {
        let v = 0;
        if (s.key === "lastTouched") {
          const ta = new Date(a.updated_at || a.created_at || 0).getTime();
          const tb = new Date(b.updated_at || b.created_at || 0).getTime();
          v = ta - tb;
        } else if (s.key === "created") {
          const ta = new Date(a.created_at || 0).getTime();
          const tb = new Date(b.created_at || 0).getTime();
          v = ta - tb;
        } else if (s.key === "appliance") {
          const sa = `${a.appliance?.brand || ""} ${a.appliance?.model_number || a.appliance?.name || ""}`.trim().toLowerCase();
          const sb = `${b.appliance?.brand || ""} ${b.appliance?.model_number || b.appliance?.name || ""}`.trim().toLowerCase();
          v = sa.localeCompare(sb);
        } else if (s.key === "id") {
          v = a.id.localeCompare(b.id);
        }
        if (v !== 0) return s.dir === "asc" ? v : -v;
      }
      return 0;
    });
    return arr;
  }, [filteredWorkOrders, sort]);


  const getProgressInfo = (order: WorkOrder) => {
    const progress = order.data;
    if (!progress || !progress.totalSteps) return null;
    const completedCount = progress.completedCount || 0;
    const totalSteps = progress.totalSteps;
    const percent = Math.round((completedCount / totalSteps) * 100);
    const phaseName = progress.currentPhaseName || "";
    const stepTitle = progress.currentStepTitle || "";
    const stepNumber = (progress.currentStepIndex ?? 0) + 1;
    return { completedCount, totalSteps, percent, phaseName, stepTitle, stepNumber };
  };

  const handleAssigned = (orderId: string, newAssigneeId: string | null) => {
    setWorkOrders((prev) =>
      prev.map((o) => (o.id === orderId ? { ...o, assignee_id: newAssigneeId } : o)),
    );
  };


  return (
    <div className="min-h-screen bg-background pb-24">

      {/* Sticky scanner search at top */}
      <div className="px-6 pt-6">
        <WorkOrderScanSearch value={searchQuery} onChange={setSearchQuery} />
      </div>

      <div className="px-6 mt-4">
        <div className="border-t border-border" />
      </div>

      {/* Header row with status tabs on the right */}
      <div className="px-6 pt-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground mb-1">Work Orders</h1>
          <p className="text-muted-foreground">
            Resume open work orders, or scan a new appliance to start
          </p>
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          {statusFilterOptions.map((opt) => {
            const isActive = statusFilter === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => setStatusFilter(opt.value)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm transition-colors ${
                  isActive
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-card text-foreground border-border hover:border-primary/50"
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${opt.dotColor}`} />
                <span>{opt.label}</span>
                <span className={`text-xs ${isActive ? "opacity-80" : "text-muted-foreground"}`}>
                  {opt.count}
                </span>
              </button>
            );
          })}
          <InventorySortMenu
            entityLabel="work orders"
            fields={WORK_ORDERS_SORT_FIELDS}
            value={sort}
            defaultValue={DEFAULT_WO_SORT}
            onChange={setSort}
          />
        </div>

      </div>

      {/* Tip line */}
      <div className="px-6 mt-3 mb-6 flex items-center gap-1.5 text-xs text-muted-foreground">
        <Lightbulb className="w-3.5 h-3.5" />
        <span>Scan the next appliance whenever you're ready — the search bar always accepts.</span>
      </div>

      {/* Work Orders List */}
      <div className="px-6">

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : workOrders.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Package className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium">No work orders yet</p>
            <p className="text-sm mt-1">Scan an appliance type shield above to start one</p>
          </div>
        ) : sortedWorkOrders.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Package className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium">No work orders match the current filter</p>
          </div>
        ) : (
          <div className="space-y-3">
            {sortedWorkOrders.map((order) => {

              const status = (order.status || "in_progress") as StatusFilter;
              const statusStyle = statusColors[status] || statusColors.in_progress;
              const label = statusLabels[status] || status.toUpperCase();
              const a = order.appliance;
              const brand = a?.brand || "";
              const modelName = a?.model_number || a?.name || "Unknown Appliance";
              const serialFull = a?.serial_number ? a.serial_number.toUpperCase() : null;
              const applianceType = a ? applianceCategoryLabel({ category: a.category, productType: a.product_type ?? a.type }) : null;
              const progressInfo = getProgressInfo(order);
              const applianceMissing = !order.appliance;
              const lastTouched = order.updated_at || order.created_at;

              const routeForOrder = () => {
                const woQuery = `?wo=${order.id}&workOrderId=${order.id}`;
                return `/appliance/${order.appliance_id}/workflow/harvesting${woQuery}`;
              };

              const handleOpenOrder = () => {
                if (applianceMissing) {
                  toast({
                    title: "Appliance not found",
                    description: "The appliance for this work order no longer exists.",
                    variant: "destructive",
                  });
                  return;
                }
                navigate(routeForOrder());
              };

              return (
                <div
                  key={order.id}
                  onClick={handleOpenOrder}
                  className={`group/card bg-card rounded-xl border border-border p-4 transition-colors ${
                    applianceMissing
                      ? "opacity-60 cursor-not-allowed"
                      : "cursor-pointer hover:border-primary/50"
                  }`}
                >
                  {/* Top row: ID + harvest icon left, assignee right */}
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="group/id flex items-center gap-1.5 min-w-0">
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded border border-green-200 bg-green-50 shrink-0">
                        <Recycle className="w-3 h-3 text-green-600" />
                      </span>
                      <span className="text-xs text-muted-foreground font-mono tracking-wide truncate">
                        FIXID-{order.id.slice(0, 8).toUpperCase()}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(order.id);
                          setCopiedId(order.id);
                          setTimeout(() => setCopiedId(null), 1500);
                        }}
                        className="p-0.5 rounded hover:bg-muted text-muted-foreground transition-opacity opacity-0 group-hover/id:opacity-100 focus:opacity-100"
                        title="Copy work order ID"
                        aria-label="Copy work order ID"
                      >
                        {copiedId === order.id ? (
                          <Check className="w-3 h-3 text-green-600" />
                        ) : (
                          <Copy className="w-3 h-3" />
                        )}
                      </button>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {isAdmin && (
                        <DropdownMenu onOpenChange={(open) => open && loadDeleteCheck(order.id)}>
                          <DropdownMenuTrigger asChild>
                            <button
                              onClick={(e) => e.stopPropagation()}
                              className="p-1 rounded-md hover:bg-muted text-muted-foreground opacity-0 group-hover/card:opacity-100 focus:opacity-100 transition-opacity"
                              aria-label="Work order actions"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {(() => {
                              const check = deleteChecks[order.id];
                              const blocked = check !== undefined && check !== "ok";
                              const item = (
                                <DropdownMenuItem
                                  disabled={check === undefined || blocked}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteReason(null);
                                    setDeleteOther("");
                                    setDeleteOrder(order);
                                  }}
                                  className="text-destructive focus:text-destructive"
                                >
                                  {check === undefined ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                                  {t("softDelete.delete")}
                                </DropdownMenuItem>
                              );
                              if (!blocked) return item;
                              return (
                                <TooltipProvider delayDuration={100}>
                                  <Tooltip>
                                    <TooltipTrigger asChild><div>{item}</div></TooltipTrigger>
                                    <TooltipContent side="left" className="max-w-xs">
                                      <span className="text-xs">{t(`softDelete.errors.${check}`)}</span>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              );
                            })()}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                      <AssigneePicker
                        workOrderId={order.id}
                        assigneeId={order.assignee_id}
                        members={members}
                        membersLoading={membersLoading}
                        onAssigned={(newId) => handleAssigned(order.id, newId)}
                        avatarSize="xs"
                      />
                    </div>
                  </div>

                  {/* Body */}
                  <div className="space-y-1">
                    {/* Row 1: brand · model (same style) */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {brand && (
                        <h3 className="font-medium text-foreground truncate">{brand}</h3>
                      )}
                      {brand && <span className="text-muted-foreground">·</span>}
                      <h3 className="font-medium text-foreground truncate">{modelName}</h3>
                    </div>

                    {/* Row 2: type tag + serial label */}
                    <div className="flex items-center gap-2 flex-wrap pt-1">
                      {applianceType && (
                        <span className="inline-block px-2 py-0.5 bg-muted text-muted-foreground text-xs rounded capitalize">
                          {applianceType}
                        </span>
                      )}
                      {serialFull && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-muted text-muted-foreground text-xs rounded">
                          <span>Serial</span>
                          <span className="font-mono">{serialFull}</span>
                        </span>
                      )}
                    </div>

                    {/* Row 3: last touched (left) + status icon (right) */}
                    <div className="flex items-center justify-between gap-2 pt-1">
                      <span className="text-xs text-muted-foreground">
                        Last touched{" "}
                        {lastTouched ? format(new Date(lastTouched), "dd MMM, yyyy HH:mm") : "—"}
                      </span>
                      <TooltipProvider delayDuration={150}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              className={`inline-flex items-center justify-center w-5 h-5 rounded-full ${statusStyle.bg} ${statusStyle.text} shrink-0`}
                              aria-label={label}
                            >
                              {status === "completed" ? (
                                <CheckCircle className="w-3.5 h-3.5" />
                              ) : (
                                <Loader className="w-3.5 h-3.5" />
                              )}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="left">
                            <span className="text-xs">
                              {label}
                              {status !== "completed" && progressInfo?.stepTitle && (
                                <> · Step {progressInfo.stepNumber} {progressInfo.stepTitle}</>
                              )}
                            </span>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete confirmation (FIX-258 soft delete) */}
      <AlertDialog open={!!deleteOrder} onOpenChange={(open) => !open && setDeleteOrder(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("softDelete.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("softDelete.mistakesOnly")} {t("softDelete.kept")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <Label>{t("softDelete.reasonLabel")}</Label>
            <RadioGroup value={deleteReason ?? ""} onValueChange={(v) => setDeleteReason(v as DeleteReason)}>
              {DELETE_REASONS.map((r) => (
                <div key={r} className="flex items-center gap-2">
                  <RadioGroupItem value={r} id={`del-reason-${r}`} />
                  <Label htmlFor={`del-reason-${r}`} className="font-normal">{t(`softDelete.reasons.${r}`)}</Label>
                </div>
              ))}
            </RadioGroup>
            {deleteReason === "other" && (
              <Textarea
                value={deleteOther}
                onChange={(e) => setDeleteOther(e.target.value)}
                placeholder={t("softDelete.otherPlaceholder")}
                maxLength={500}
              />
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>{t("softDelete.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDelete(); }}
              disabled={isDeleting || !buildDeleteReason(deleteReason, deleteOther)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting && <Loader2 className="w-4 h-4 animate-spin" />}
              {t("softDelete.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
